import React, { useEffect, useMemo, useState } from 'react';
import { fetchAdminTestHistory } from '../../utils/testApi';

const HISTORY_PAGE_SIZE = 50;

function TestAnswerHistoryManagement() {
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [themeSummaries, setThemeSummaries] = useState([]);
  const [overallSummary, setOverallSummary] = useState({
    answer_count: 0,
    average_score: 0,
    correct_rate: 0,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedThemeId, setSelectedThemeId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const totalPages = useMemo(() => {
    const pages = Math.ceil(total / HISTORY_PAGE_SIZE);
    return pages > 0 ? pages : 1;
  }, [total]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');

        const params = {
          search,
          users_limit: 200,
          limit: HISTORY_PAGE_SIZE,
          offset: (page - 1) * HISTORY_PAGE_SIZE,
        };

        if (selectedUserId) {
          params.user_id = Number(selectedUserId);
        }
        if (selectedThemeId) {
          params.theme_id = Number(selectedThemeId);
        }

        const result = await fetchAdminTestHistory(params);
        const data = result.data || {};

        setUsers(data.users || []);

        // 初回は回答履歴があるユーザーを自動選択して表示を開始する。
        if (!selectedUserId && (data.users || []).length > 0) {
          const firstWithAnswers = data.users.find((user) => Number(user.answer_count) > 0);
          const firstUser = firstWithAnswers || data.users[0];
          setSelectedUserId(String(firstUser.user_id));
          setPage(1);
          setSelectedThemeId('');
          setLoading(false);
          return;
        }

        setSelectedUser(data.selected_user || null);
        setHistory(data.history || []);
        setThemeSummaries(data.theme_summaries || []);
        setTotal(Number(data.total) || 0);
        setOverallSummary(data.overall_summary || {
          answer_count: 0,
          average_score: 0,
          correct_rate: 0,
        });
      } catch (err) {
        setError(err.message || '回答履歴の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search, selectedUserId, selectedThemeId, page]);

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
    setSelectedThemeId('');
    setPage(1);
  };

  const handleThemeChange = (e) => {
    setSelectedThemeId(e.target.value);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Webテスト回答履歴管理</h1>
        <p className="text-sm text-gray-600 mt-2">管理者向けに、ユーザーごとの回答履歴とテーマ別成績を確認できます。</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="ユーザー検索（名前・ID）"
          />

          <select
            value={selectedUserId}
            onChange={handleUserChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">ユーザーを選択してください</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.user_name} ({user.login_id}) / {user.answer_count}件 / 平均{Number(user.average_score || 0).toFixed(1)}点
              </option>
            ))}
          </select>

          <select
            value={selectedThemeId}
            onChange={handleThemeChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            disabled={!selectedUserId}
          >
            <option value="">全テーマ</option>
            {themeSummaries.map((theme) => (
              <option key={theme.theme_id} value={theme.theme_id}>
                {theme.theme_name}
              </option>
            ))}
          </select>
        </div>

        {selectedUser && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-900">
              対象ユーザー: {selectedUser.user_name} ({selectedUser.login_id})
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs px-2 py-1 rounded bg-white border border-blue-100 text-blue-700">
                回答数: {overallSummary.answer_count}件
              </span>
              <span className="text-xs px-2 py-1 rounded bg-white border border-blue-100 text-blue-700">
                平均点: {Number(overallSummary.average_score || 0).toFixed(1)}点
              </span>
              <span className="text-xs px-2 py-1 rounded bg-white border border-blue-100 text-blue-700">
                正答率: {Number(overallSummary.correct_rate || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        )}

        {selectedUser && themeSummaries.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-800">テーマ別サマリー</p>
            <div className="flex flex-wrap gap-2">
              {themeSummaries.map((theme) => (
                <div key={theme.theme_id} className="px-3 py-2 rounded bg-gray-50 border border-gray-200">
                  <p className="text-xs text-gray-700 font-medium">{theme.theme_name}</p>
                  <p className="text-sm text-gray-900">平均: {Number(theme.average_score || 0).toFixed(1)}点</p>
                  <p className="text-xs text-gray-600">{theme.answer_count}件 / 正答率 {Number(theme.correct_rate || 0).toFixed(1)}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : !selectedUserId ? (
          <p className="text-sm text-gray-500">ユーザーを選択すると回答履歴が表示されます。</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">該当する回答履歴がありません。</p>
        ) : (
          <div className="space-y-3">
            {history.map((item) => (
              <div key={item.answer_id} className="border border-gray-200 rounded-md p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{item.theme_name}</p>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">score: {item.score}</span>
                  <span className={`text-xs px-2 py-1 rounded ${item.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {item.correct ? 'correct' : 'incorrect'}
                  </span>
                </div>
                <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">Q. {item.question_text}</p>
                <p className="text-sm text-blue-800 mt-2 whitespace-pre-wrap">模範解答: {item.model_answer}</p>
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">A. {item.answer_text}</p>
                <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">feedback: {item.feedback}</p>
                <p className="text-xs text-gray-500 mt-2">{new Date(item.created_at).toLocaleString('ja-JP')}</p>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
              >
                前へ
              </button>
              <p className="text-xs text-gray-500">
                {page} / {totalPages} ページ（全 {total} 件）
              </p>
              <button
                type="button"
                onClick={() => setPage((prev) => (prev < totalPages ? prev + 1 : prev))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50"
              >
                次へ
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TestAnswerHistoryManagement;
